import api from '../services/api';

/**
 * Get RFQs with pagination and filtering
 * @param {Object} params - Query parameters object
 * @returns {Promise<Object>} API response
 */
export const getRFQs = async (params = {}) => {
  try { 
    const response = await api.get('/api/procurement', { params });
 
    
    // Handle ApiProcurement response format: { status: 1, data: {...}, message: '...' }
    if (response.data && response.data.status !== undefined) {
      if (response.data.status === 0) {
        // Handle error cases like permission denied
        return {
          status: 0,
          data: { data: [], meta: { totalItems: 0, currentPage: 1, itemsPerPage: 0, totalPages: 0 } },
          message: response.data.message || 'Access denied'
        };
      }
      
      // Check if the response has the new paginated format with meta
      if (response.data.data && response.data.data.meta) {
        // New paginated format: { status: 1, data: { data: [...], meta: {...} }, message: '...' }
        return {
          status: response.data.status,
          data: response.data.data, // This already contains { data: [...], meta: {...} }
          message: response.data.message || ''
        };
      } else {
        // Legacy format: { status: 1, data: [...], message: '...' }
        const rfqsData = response.data.data || [];
        return {
          status: response.data.status,
          data: {
            data: rfqsData,
            meta: {
              totalItems: rfqsData.length,
              currentPage: 1,
              itemsPerPage: rfqsData.length,
              totalPages: 1
            }
          },
          message: response.data.message || ''
        };
      }
    } else {
      return response.data;
    }
  } catch (error) {
    console.error('Error fetching RFQs:', error);
    
    // Handle specific error cases
    if (error.response?.status === 403) {
      return {
        status: 0,
        data: { data: [], meta: { totalItems: 0, currentPage: 1, itemsPerPage: 0, totalPages: 0 } },
        message: 'You do not have permission to view RFQs'
      };
    }
    
    throw error;
  }
};

/**
 * Get a single RFQ by ID
 * @param {string|number} rfqId - RFQ ID
 * @returns {Promise<Object>} API response
 */
export const getRFQ = async (rfqId) => {
  try {
    const response = await api.get(`/api/procurement/${rfqId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching RFQ:', error);
    throw error;
  }
};

/**
 * Create a new RFQ
 * @param {Object} rfqData - RFQ data
 * @returns {Promise<Object>} API response
 */
export const createRFQ = async (rfqData) => {
  try {
    const response = await api.post('/api/procurement', rfqData);
    return response.data;
  } catch (error) {
    console.error('Error creating RFQ:', error);
    throw error;
  }
};

/**
 * Update an existing RFQ
 * @param {string|number} rfqId - RFQ ID
 * @param {Object} rfqData - Updated RFQ data
 * @returns {Promise<Object>} API response
 */
export const updateRFQ = async (rfqId, rfqData) => {
  try {
    const response = await api.put(`/api/procurement/${rfqId}`, rfqData);
    return response.data;
  } catch (error) {
    console.error('Error updating RFQ:', error);
    throw error;
  }
};

/**
 * Delete an RFQ
 * @param {string|number} rfqId - RFQ ID
 * @returns {Promise<Object>} API response
 */
export const deleteRFQ = async (rfqId) => {
  try {
    const response = await api.delete(`/api/procurement/${rfqId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting RFQ:', error);
    throw error;
  }
};

/**
 * Get RFQ quotes/suppliers
 * @param {string|number} rfqId - RFQ ID
 * @returns {Promise<Object>} API response
 */
export const getRFQQuotes = async (rfqId) => {
  try {
    const response = await api.get(`/api/procurement/submissions/${rfqId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching RFQ quotes:', error);
    throw error;
  }
};

/**
 * Publish/Activate an RFQ
 * @param {string|number} rfqId - RFQ ID
 * @returns {Promise<Object>} API response
 */
export const publishRFQ = async (rfqId) => {
  try {
    const response = await api.post(`/api/procurement/submit/${rfqId}`);
    return response.data;
  } catch (error) {
    console.error('Error publishing RFQ:', error);
    throw error;
  }
};

/**
 * Close an RFQ
 * @param {string|number} rfqId - RFQ ID
 * @returns {Promise<Object>} API response
 */
export const closeRFQ = async (rfqId) => {
  try {
    const response = await api.post(`/api/procurement/${rfqId}/close`);
    return response.data;
  } catch (error) {
    console.error('Error closing RFQ:', error);
    throw error;
  }
};

/**
 * Get detailed quotation/submission information
 * @param {string} supplierCode - Supplier code (SID)
 * @param {string|number} rfqId - RFQ ID
 * @returns {Promise<Object>} API response
 */
export const getQuotationDetails = async (supplierCode, rfqId) => {
  try {
    const response = await api.post('/api/procurement/submission', {
      SID: supplierCode,
      RFQ: rfqId
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching quotation details:', error);
    throw error;
  }
};

/**
 * Send RFQ to suppliers
 * @param {string|number} rfqId - RFQ ID
 * @param {string} supplierCode - Optional supplier code to send to specific supplier
 * @returns {Promise<Object>} API response
 */
export const sendRFQ = async (rfqId, supplierCode = null) => {
  try {
    const url = supplierCode 
      ? `/api/procurement/send/${rfqId}?supplier_code=${supplierCode}`
      : `/api/procurement/send/${rfqId}`;
    const response = await api.post(url);
    return response.data;
  } catch (error) {
    console.error('Error sending RFQ:', error);
    throw error;
  }
};

/**
 * Award RFQ to a supplier
 * @param {string|number} rfqId - RFQ ID
 * @param {string|number} supplierId - Supplier ID
 * @param {Object} awardData - Award details
 * @returns {Promise<Object>} API response
 */
export const awardRFQ = async (rfqId, supplierId, awardData) => {
  try {
    const response = await api.post(`/api/procurement/${rfqId}/award`, {
      supplier_id: supplierId,
      ...awardData,
    });
    return response.data;
  } catch (error) {
    console.error('Error awarding RFQ:', error);
    throw error;
  }
};
